<template>
  <v-row>
    <v-col
      sm="3"
      class="hidden-xs-only"
    >
      <div style="position: sticky; top: 20px">
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.label"
          label="Label"
          solo
          clearable
          hide-details
          class="mb-2"
        />
        <v-text-field
          v-model="filtersDirty.code"
          label="Code"
          solo
          clearable
          hide-details
          class="mb-2"
        />
        <v-autocomplete
          v-model="filtersDirty.contractor_id"
          solo
          label="Supplier"
          clearable
          :items="contractors.data"
          item-text="name"
          item-value="id"
          :loading="contractors.loading"
        />
      </div>
    </v-col>
    <v-col
      cols="16"
      sm="13"
    >
      <pagination
        :from="inventoryItems.meta.from"
        :to="inventoryItems.meta.to"
        :total="inventoryItems.meta.total"
        :last-page="inventoryItems.meta.last_page"
        :page.sync="pagination.page"
        class="align-center mb-2"
      />
      <v-data-table
        :headers="inventoryItems.headers"
        :items="inventoryItems.data"
        :page.sync="pagination.page"
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.desc"
        :loading="inventoryItems.loading"
        :server-items-length="inventoryItems.meta.total"
        item-key="id"
        must-sort
        hide-default-footer
        class="elevation-5"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td class="font-weight-medium">
              {{ item.label }}
            </td>
            <td>
              <flag
                :iso="item.country.code"
                :squared="false"
              />
              <span
                v-if="item.base"
                class="ml-1"
              >
                {{ item.base.name }}
              </span>
            </td>
            <td>
              <span
                v-if="item.code"
                class="grey--text"
              >
                {{ item.code }}
              </span>
              <span
                v-else
                class="grey--text"
              >
                n/a
              </span>
            </td>
            <td :class="{ 'error--text': !item.stock }">
              {{ item.stock | currency('') }} <span class="grey--text">
                {{ item.unit }}
              </span>
            </td>
            <td>
              {{ item.unit_price | currency(item.currency) }}
            </td>
            <td>
              <span v-if="item.contractor">
                {{ item.contractor.name }}
              </span>
              <span
                v-else
                class="grey--text"
              >
                n/a
              </span>
            </td>
            <td class="text-no-wrap">
              <v-btn
                small
                text
                rounded
                color="grey"
                class="text-none ma-0"
                @click="openInventoryItemAddStockDialog(item)"
              >
                <v-icon
                  small
                  class="mr-1"
                >
                  fa fa-cart-plus
                </v-icon>
                Add
              </v-btn>
              <v-btn
                small
                text
                rounded
                color="grey"
                class="text-none ma-0"
                @click="openInventoryItemEditDialog(item)"
              >
                <v-icon small>
                  edit
                </v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
        <template slot="no-data">
          <!--<editor-fold desc="Filtered">-->
          <div
            v-if="isFiltered"
            class="text-center py-4"
          >
            <div>
              There are no results for your search.
            </div>
            <div
              class="mt-2"
            >
              <v-btn
                small
                text
                @click="clearFilters"
              >
                <v-icon left>
                  clear
                </v-icon>
                Clear Filters
              </v-btn>
            </div>
          </div>
          <!--</editor-fold>-->
          <!--<editor-fold desc="NoData">-->
          <div
            v-else
            class="text-center py-4"
          >
            <v-col cols="16">
              There are no inventory items at the moment.
            </v-col>
            <v-col
              v-if="$can('create', 'InventoryItem')"
              cols="16"
              class="mt-2"
            >
              <v-btn
                color="primary"
                small
                @click="openInventoryItemEditDialog()"
              >
                <v-icon left>
                  add
                </v-icon>
                Add New
              </v-btn>
            </v-col>
          </div>
        <!--</editor-fold>-->
        </template>
      </v-data-table>
      <pagination
        :from="inventoryItems.meta.from"
        :to="inventoryItems.meta.to"
        :total="inventoryItems.meta.total"
        :last-page="inventoryItems.meta.last_page"
        :page.sync="pagination.page"
        class="align-center mt-2"
      />
    </v-col>

    <inventory-item-edit-dialog
      :visible.sync="inventoryItemEditDialog"
      :inventory-item="inventoryItemDirty"
      @inventoryItem:created="inventoryItemCreated"
      @inventoryItem:updated="inventoryItemUpdated"
      @inventoryItem:deleted="inventoryItemDeleted"
    />

    <inventory-add-stock-dialog
      v-if="inventoryItemDirty"
      :visible.sync="inventoryAddStockDialog"
      :inventory-item="inventoryItemDirty"
      @inventoryItem:updated="inventoryItemUpdated"
    />

    <inventory-issue-dialog
      v-if="inventoryItemDirty"
      :visible.sync="inventoryIssueDialog"
      :inventory-item="inventoryItemDirty"
      @inventoryItem:updated="inventoryItemUpdated"
    />
  </v-row>
</template>

<script>
  import cloneDeep from 'lodash/cloneDeep'
  import serviceCurrentLocationMixin from '@/mixins/serviceCurrentLocationMixin'
  import paginationMixin from '@/mixins/paginationMixin'
  import filterableMixin from '@/mixins/filterableMixin'
  import Contractor from '@/models/Contractor'
  import InventoryItem from '@/models/InventoryItem'
  import InventoryItemEditDialog from '@/components/service/inventory/InventoryItemEditDialog'
  import InventoryAddStockDialog from '@/components/service/inventory/InventoryAddStockDialog'
  import InventoryIssueDialog from '@/components/service/inventory/InventoryIssueDialog'

  export default {
    name: 'InventoryItems',
    components: {
      InventoryIssueDialog,
      InventoryAddStockDialog,
      InventoryItemEditDialog
    },
    mixins: [paginationMixin, filterableMixin, serviceCurrentLocationMixin],
    data() {
      return {
        inventoryItems: {
          headers: [
            { text: 'Label', value: 'label' },
            { text: 'Available in', value: 'country_id' },
            { text: 'Code', value: 'code' },
            { text: 'Stock', value: 'stock' },
            { text: 'Unit Price', value: 'unit_price' },
            { text: 'Supplier', value: 'contractor_id' },
            { sortable: false, width: '1%' }
          ],
          data: [],
          loading: false,
          meta: {}
        },
        pagination: {
          perPage: 30
        },
        inventoryItemDirty: undefined,
        inventoryItemEditDialog: false,
        inventoryAddStockDialog: false,
        inventoryIssueDialog: false,
        filtersClean: cloneDeep(InventoryItem.emptyFilters),
        filtersDirty: cloneDeep(InventoryItem.emptyFilters)
      }
    },
    computed: {
      contractors() {
        return {
          loading: this.$store.state.service.contractors.loading,
          data: this.$store.state.service.contractors.data.filter(c => c.type === Contractor.TYPE_SUPPLIER)
        }
      }
    },
    created() {
      this.fetchInventoryItems()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchInventoryItems()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      onCurrentLocationUpdate() {
        this.resetPagination()
      },
      async fetchInventoryItems() {
        this.inventoryItems.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams,
          ...this.currentLocationQueryParams
        }

        try {
          const result = await InventoryItem.api.fetch(queryParams)

          this.inventoryItems.data = result.data
          this.inventoryItems.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.inventoryItems.loading = false
        }
      },
      inventoryItemCreated(inventoryItem) {
        this.inventoryItems.data.unshift({ ...inventoryItem, new: true })
      },
      inventoryItemUpdated(inventoryItem) {
        const index = this.inventoryItems.data.findIndex(ii => ii.id === inventoryItem.id)
        this.inventoryItems.data.splice(index, 1, inventoryItem)
      },
      inventoryItemDeleted(inventoryItem) {
        const index = this.inventoryItems.data.findIndex(ii => ii.id === inventoryItem.id)
        this.inventoryItems.data.splice(index, 1)
      },
      openInventoryItemEditDialog(inventoryItem = undefined) {
        this.inventoryItemDirty = inventoryItem
        this.inventoryItemEditDialog = true
      },
      openInventoryItemDeleteDialog(inventoryItem) {
        this.inventoryItemDirty = inventoryItem
        this.toggleConfirmationDialog(true)
      },
      openInventoryItemAddStockDialog(inventoryItem) {
        this.inventoryItemDirty = inventoryItem
        this.inventoryAddStockDialog = true
      },
      openInventoryItemIssueDialog(inventoryItem) {
        this.inventoryItemDirty = inventoryItem
        this.inventoryIssueDialog = true
      },
      async deleteInventoryItem() {
        this.deleting = true

        try {
          await InventoryItem.api.destroy(this.inventoryItemDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Inventory item deleted',
            'color': 'warning'
          })
          this.fetchInventoryItems()
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      }
    }
  }
</script>
